import { inject, Injectable, OnDestroy } from '@angular/core';

import { Clock } from '../clock/clock';
import { Disposable } from '../clock/interfaces';
import { RefreshHandler } from './models';
import { RefreshTier } from '../config/enums';

export const REFRESH_TIERS: readonly RefreshTier[] = [RefreshTier.Main, RefreshTier.Secondary, RefreshTier.Config];
export const REFRESH_INTERVALS: Readonly<Record<RefreshTier, number>> = {
  main: 10 * 60 * 1000, secondary: 35 * 60 * 1000, config: 60 * 60 * 1000
};
// Spreads displays that booted together across the interval, so the API never takes a whole site at once.
export const REFRESH_JITTER_MS = 5 * 60 * 1000;

// Owns the cadence of all three tiers: news and activities move far faster than the channel configuration does.
@Injectable()
export class RefreshSchedulerService implements OnDestroy {
  private readonly clock = inject(Clock);
  private readonly tickers = new Map<RefreshTier, Disposable>();
  private readonly inFlight = new Set<RefreshTier>();
  private handler?: RefreshHandler;
  private controller?: AbortController;

  public begin(handler: RefreshHandler): void {
    this.stop();
    this.handler = handler;
    this.controller = new AbortController();

    for (const tier of REFRESH_TIERS) {
      this.tickers.set(tier, this.clock.every(REFRESH_INTERVALS[tier], REFRESH_JITTER_MS, () => void this.run(tier)));
    }
  }

  public async run(...tiers: readonly RefreshTier[]): Promise<void> {
    await Promise.all(tiers.map(tier => this.runOne(tier)));
  }

  public stop(): void {
    this.controller?.abort();
    this.controller = undefined;
    this.handler = undefined;
    this.tickers.forEach(ticker => ticker.dispose());
    this.tickers.clear();
    this.inFlight.clear();
  }

  public ngOnDestroy(): void {
    this.stop();
  }

  private async runOne(tier: RefreshTier): Promise<void> {
    // A tier still fetching skips its tick instead of stacking a second fan-out on a slow network.
    if (!this.handler || !this.controller || this.inFlight.has(tier)) {
      return;
    }

    const handler = this.handler;
    const signal = this.controller.signal;
    this.inFlight.add(tier);

    try {
      await handler(tier, signal);
    } catch {
      // Feed-level isolation lives in the handler; a rejected tier must not stop the schedule.
    } finally {
      this.inFlight.delete(tier);
    }
  }
}
